// ————————————————————————————————————————————— LIB. ————————————————————————————————————————————— //
// ———————————————————————————————————————————————————————————————————————————————————————————————— //

import Component from "../../../../app/lib/jGia/jGia/src/Component";
import eventbus from "../../../../app/lib/jGia/jGia/src/eventbus";

// ———————————————————————————————————————————— UTIL. ————————————————————————————————————————————— //
// ———————————————————————————————————————————————————————————————————————————————————————————————— //

import logger from "../../../../app/baseUtilities/logger";
import validate_refEl from "../../../../app/baseUtilities/validate/validate_refEl.js";
import cancel_featureInit from "../../../../app/baseUtilities/cancel/cancel_featureInit";
import cancel_ebh from "../../../../app/baseUtilities/cancel/cancel_ebh.js";

// —————————————————————————————————————— INITIALIZATION F() —————————————————————————————————————— //
// ———————————————————————————————————————————————————————————————————————————————————————————————— //

import init_loader from "./init/init_loader.js";
import init_swupHooker from "./init/init_swupHooker.js";

// ——————————————————————————— EVENT/EVENTBUS/STATE CHANGE HANDLERS/API ——————————————————————————— //
// ———————————————————————————————————————————————————————————————————————————————————————————————— //

import stChH_hidden from "./stateChangeHandlers/stChH_hidden.js";
import ebh_window_resize from "./eventbusHandlers/ebh_window_resize.js";

import stChH_mode from "./stateChangeHandlers/stChH_mode.js";
import stChH_loadProgress from "./stateChangeHandlers/stChH_loadProgress.js";
import stChH_loadedItems from "./stateChangeHandlers/stChH_loadedItems.js";

import get_preloadManifest from "./api/get_preloadManifest.js";
import load_assets from "./api/load_assets.js";
import insert_loadedAsset from "./api/insert_loadedAsset.js";

// ———————————————————————————————————————————— ASSETS ———————————————————————————————————————————— //
// ———————————————————————————————————————————————————————————————————————————————————————————————— //

// Options with case-sensitive keys that are not to be
// automatically extracted from the options arg. but be
// assigned to an option key manually to preserve its case
// (Kirby CMS converts all fields/option keys to lowercase)
// (see constructor).

const manualOptionKeys = ["pagerevealdelay"];

//  Default values for manually extracted options
//  (see constructor, in case specific option has not been provided
//  in comp. config.).

const defaultOptions = { pageRevealDelay: 0 };

// ———————————————————————————————————————————————————————————————————————————————————————————————— //
// ———————————————————————————————————————————————————————————————————————————————————————————————— //

class Preloader extends Component {
  ///////////////////////////// Constructor //////////////////////////////
  ////////////////////////////////////////////////////////////////////////

  constructor(element, options) {
    super(element);

    ///////// DOM references //////////
    ///////////////////////////////////

    this.ref = {
      progressBar: null,
      progressNumber: null,
    };

    //////////// Options /////////////
    //////////////////////////////////

    // Get options not to be manually extracted from the options arg...
    const autoOptions = {};
    for (const key in options) if (!manualOptionKeys.includes(key)) autoOptions[key] = options[key];

    this.options = {
      name: "Preloader",
      version: element.getAttribute("g-version") ?? "1",
      pageRevealDelay: options.pagerevealdelay ?? defaultOptions.pageRevealDelay,
      ...autoOptions,
    };

    //////////// Utilities /////////////
    ////////////////////////////////////

    this.logger = logger.bind(this);
    this.validate_refEl = validate_refEl.bind(this);
    this.cancel_featInit = cancel_featureInit.bind(this);
    this.cancel_ebh = cancel_ebh.bind(this);

    //////////// Init. f() /////////////
    ////////////////////////////////////

    this.init_swupHooker = init_swupHooker.bind(this);
    this.init_loader = init_loader.bind(this);

    ///////////// Modules //////////////
    ////////////////////////////////////

    this.modules = {
      loadQueue: { instance: this.init_loader() },
    };

    /////////////// API ////////////////
    ////////////////////////////////////

    this.api = {
      insert_loadedAsset: insert_loadedAsset.bind(this),
      load_assets: load_assets.bind(this),
      get_preloadManifest: get_preloadManifest.bind(this),
      hideComp: () => this.setState({ hidden: true }),
      showComp: () => this.setState({ hidden: false }),
    };

    //////// Eventbus listeners ////////
    ////////////////////////////////////

    this.ebl_window_resize = ebh_window_resize.bind(this);

    ////// State-change listeners //////
    ////////////////////////////////////

    this.stChL_hidden = stChH_hidden.bind(this);
    this.stChL_loadedItems = stChH_loadedItems.bind(this);
    this.stChL_loadProgress = stChH_loadProgress.bind(this);
    this.stChL_mode = stChH_mode.bind(this);

    ////// Custom event handlers ///////
    // (To be passed to parent class) //

    // ...

    ///////// Pre-mount init. //////////
    ////////////////////////////////////

    // ...
  }

  //////////////////////////////// Mount /////////////////////////////////
  ////////////////////////////////////////////////////////////////////////

  mount() {
    this.logger("info", ["mounting"], "action", { eventName: "mount", inline: true });
    this.init();
  }

  /////////////////////////////// Unmount ////////////////////////////////
  ////////////////////////////////////////////////////////////////////////

  unmount() {
    this.logger("info", ["unmounting"], "action", { eventName: "unmount", inline: true });

    /////////////////////////////
    // Listener deregistration //
    /////////////////////////////

    eventbus.off("window_resize", this.ebl_window_resize);

    /////////////////////////////
    // API call deregistration //
    /////////////////////////////

    eventbus.off("Preloader.api.insert_loadedAsset", this.api.insert_loadedAsset);
    eventbus.off("Preloader.api.load_assets", this.api.load_assets);
    eventbus.off("Preloader.api.get_preloadManifest", this.api.get_preloadManifest);
    eventbus.off("Preloader.api.hideComp", this.api.hideComp);
    eventbus.off("Preloader.api.showComp", this.api.showComp);
  }

  ///////////////////////////////// Init. ////////////////////////////////
  ////////////////////////////////////////////////////////////////////////

  async init() {
    this.setState({ mode: "init" });
    this.init_swupHooker();
    this.init_states();
    this.init_eventbus();
    this.setState({ mode: "ready" });

    // Load assets of init. route...
    const api = this.api;
    const { initRoute } = this.options;
    const { is_mobile } = this.state;
    try {
      const manifest = await api.get_preloadManifest(initRoute);
      const manifest_hasFiles = is_mobile ? manifest && manifest.mobileFiles?.length : manifest && manifest.desktopFiles?.length;
      const filesToLoad = manifest_hasFiles ? (is_mobile ? manifest.mobileFiles : manifest.desktopFiles) : [];
      this.logger("info", ["fetched manifest for init. route", manifest], "action", { eventName: "*" });
      if (manifest_hasFiles) api.load_assets(filesToLoad);
      else {
        this.logger("warning", ["no files to load for init. route"], "warning", { eventName: "*", inline: true });
        this.setState({ loadProgress: 100, mode: "loadingComplete" });
      }
    } catch (error) {
      this.logger("error", ["failed to fetch manifest for init. route", error], "error", { eventName: "*" });
    }
  }

  ////////////////////////////////////
  ////////////////////////////////////

  init_states() {
    this.logger("init", ["states"], "action", { eventName: "init_states", inline: true });
    this.setState({
      is_mobile: window.innerWidth < 640,
      loadedItems: [],
    });
  }

  ////////////////////////////////////
  ////////////////////////////////////

  init_eventbus() {
    this.logger("init", ["eventbus"], "action", { eventName: "init_eventbus", inline: true });

    ///////////////////////////
    // Listener registration //
    ///////////////////////////

    eventbus.on("window_resize", this.ebl_window_resize);

    ///////////////////////////
    // API call registration //
    ///////////////////////////

    eventbus.on("Preloader.api.insert_loadedAsset", this.api.insert_loadedAsset);
    eventbus.on("Preloader.api.load_assets", this.api.load_assets);
    eventbus.on("Preloader.api.get_preloadManifest", this.api.get_preloadManifest);
    eventbus.on("Preloader.api.hideComp", this.api.hideComp);
    eventbus.on("Preloader.api.showComp", this.api.showComp);
  }

  /////////////////////////// State management ///////////////////////////
  ////////////////////////////////////////////////////////////////////////

  stateChange(CHANGES) {
    ////////////// hidden //////////////
    ////////////////////////////////////

    if ("hidden" in CHANGES) this.stChL_hidden(CHANGES);

    /////////// loadedItems ///////////
    ////////////////////////////////////

    if ("loadedItems" in CHANGES) this.stChL_loadedItems(CHANGES);

    /////////// loadProgress ///////////
    ////////////////////////////////////

    if ("loadProgress" in CHANGES) this.stChL_loadProgress(CHANGES);

    /////////////// Mode ///////////////
    ////////////////////////////////////

    if ("mode" in CHANGES) this.stChL_mode(CHANGES);
  }
}

// ———————————————————————————————————————————————————————————————————————————————————————————————— //
// ———————————————————————————————————————————————————————————————————————————————————————————————— //

export default Preloader;
